import React, { useState, useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "../css/dashboard.css";
import "../css/tracker.css";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/navbar";

const COLUMNS = ["Saved", "Applied", "Interviewing", "Offers"];

export default function JobTracker() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState(() => {
    const savedJobs = localStorage.getItem("resumate_jobs");
    return savedJobs ? JSON.parse(savedJobs) : [];
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newJob, setNewJob] = useState({
    company: "",
    role: "",
    status: "Saved",
  });

  const [user] = useState(() => JSON.parse(localStorage.getItem("user")));
  const userName = user?.name ? user.name.split(" ")[0] : "Guest";

  // NEW: Subscription State
  const [isPro, setIsPro] = useState(false);
  const [showProModal, setShowProModal] = useState(false);

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  useEffect(() => {
    if (!user) {
      navigate("/auth");
    }
  }, [user, navigate]);

  // NEW: Fetch user status to determine if they are Pro
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/status`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setIsPro(data.isPro);
        }
      } catch (err) {
        console.error("Failed to fetch user status", err);
      }
    };
    fetchStatus();
  }, [API_URL]);

  useEffect(() => {
    localStorage.setItem("resumate_jobs", JSON.stringify(jobs));
  }, [jobs]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/logout`, { method: "POST" });
      localStorage.removeItem("user");
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleAddJob = (e) => {
    e.preventDefault();
    if (!newJob.company.trim() || !newJob.role.trim()) return;

    const jobEntry = {
      ...newJob,
      id: Date.now().toString(),
      dateAdded: `Added ${new Date().toLocaleDateString("en-GB", { month: "short", day: "numeric" })}`,
    };

    setJobs([...jobs, jobEntry]);
    setIsModalOpen(false);
    setNewJob({ company: "", role: "", status: "Saved" });
  };

  const handleStatusChange = (jobId, newStatus) => {
    setJobs(
      jobs.map((job) =>
        job.id === jobId ? { ...job, status: newStatus } : job,
      ),
    );
  };

  const handleDelete = (jobId) => {
    setJobs(jobs.filter((job) => job.id !== jobId));
  };

  return (
    <>
      <Helmet>
        <title>Resumify | Job Tracker</title>
        <meta
          name="description"
          content="Manage your job application pipeline from saved to hired. Track your interviews and offers with the Resumify Kanban board."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Toaster position="bottom-right" />
      <Navbar />
      <div className="dashboard-layout">
        {/* MAIN CONTENT AREA */}
        <main className="dashboard-main tracker-main">
          <header className="main-header tracker-header">
            <div className="header-greeting">
              <h1>Job Tracker</h1>
              <p>
                Manage your pipeline from initial application to final offer.
              </p>
            </div>
            <button
              className="cta-button primary-pulse"
              onClick={() => setIsModalOpen(true)}
            >
              <span className="plus-icon">+</span> Add Job
            </button>
          </header>

          <div className="kanban-board">
            {COLUMNS.map((colName) => {
              const columnJobs = jobs.filter((job) => job.status === colName);

              return (
                <div
                  key={colName}
                  className={`kanban-column col-${colName.toLowerCase()}`}
                >
                  <div className="column-header">
                    <h3>
                      <span className="col-indicator"></span>
                      {colName}
                    </h3>
                    <span className="job-count">{columnJobs.length}</span>
                  </div>

                  <div className="kanban-cards-container">
                    {columnJobs.map((job) => (
                      <div key={job.id} className="kanban-card">
                        <div className="card-top">
                          <div className="company-info">
                            <div className="company-logo">
                              {job.company.charAt(0)}
                            </div>
                            <h4 className="company-name">{job.company}</h4>
                          </div>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(job.id)}
                            title="Delete Job"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>

                        <p className="role-title">{job.role}</p>

                        <div className="card-footer-actions">
                          <span className="date-tag">🕒 {job.dateAdded}</span>

                          <div className="card-status-dropdown">
                            <label
                              className="sr-only"
                              htmlFor={`status-${job.id}`}
                            >
                              Update Status
                            </label>
                            <select
                              id={`status-${job.id}`}
                              className={`status-pill badge-${job.status.toLowerCase()}`}
                              value={job.status}
                              onChange={(e) =>
                                handleStatusChange(job.id, e.target.value)
                              }
                            >
                              {COLUMNS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </main>
      </div>

      {/* PRO SUBSCRIPTION POPUP MODAL */}
      {showProModal && (
        <div
          className="custom-modal-overlay"
          onClick={() => setShowProModal(false)}
          style={{ zIndex: 1000 }}
        >
          <div
            className="custom-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-icon-danger"
              style={{ fontSize: "3rem", marginBottom: "10px" }}
            >
              💎
            </div>
            <h3>Pro Feature</h3>
            <p>
              The AI Interview Simulator is an exclusive feature for Pro users.
              Upgrade your account to practice tailored questions and get
              real-time feedback.
            </p>
            <div
              className="custom-modal-actions"
              style={{ flexDirection: "column", gap: "10px" }}
            >
              <button
                className="modal-btn"
                style={{ background: "#4f46e5", color: "white" }}
                onClick={() => navigate("/pricing")}
              >
                Upgrade to Pro
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setShowProModal(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD JOB MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">💼</div>
                <h2>Track New Application</h2>
              </div>
              <button
                className="close-btn"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddJob} className="modal-form">
              <div className="input-group">
                <label htmlFor="company">Company Name</label>
                <input
                  id="company"
                  type="text"
                  required
                  placeholder="e.g. Stripe, Netflix, Google"
                  value={newJob.company}
                  onChange={(e) =>
                    setNewJob({ ...newJob, company: e.target.value })
                  }
                  autoFocus
                />
              </div>

              <div className="input-group">
                <label htmlFor="role">Role / Title</label>
                <input
                  id="role"
                  type="text"
                  required
                  placeholder="e.g. Senior Frontend Engineer"
                  value={newJob.role}
                  onChange={(e) =>
                    setNewJob({ ...newJob, role: e.target.value })
                  }
                />
              </div>

              <div className="input-group">
                <label htmlFor="status">Pipeline Stage</label>
                <div className="select-wrapper">
                  <select
                    id="status"
                    value={newJob.status}
                    onChange={(e) =>
                      setNewJob({ ...newJob, status: e.target.value })
                    }
                  >
                    {COLUMNS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="cta-button">
                  Save Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
